import React, { ReactNode, useEffect, useRef } from 'react';
import { Box, Flex, Spinner, Text } from 'theme-ui';

export interface BorderedGridColumn<T> {
  id: string;
  header: ReactNode;
  align?: 'left' | 'center' | 'right';
  px?: string | number;
  cell: (item: T, index: number) => ReactNode;
}

export interface BorderedGridTableTabs {
  list: string[];
  active: string;
  onChange: (tab: string) => void;
}

export interface BorderedGridTableProps<T> {
  columns: BorderedGridColumn<T>[];
  data: T[];
  gridTemplateColumns: string;
  keyExtractor: (item: T, index: number) => string | number;
  sectionBg: string;
  textColor: string;
  subTextColor: string;
  emptyText?: string;

  // Optional Card Container & Header props
  title?: ReactNode;
  subtitle?: ReactNode;
  actionText?: ReactNode;
  onActionClick?: () => void;
  actionInput?: ReactNode;
  tabs?: BorderedGridTableTabs;
  isLoading?: boolean;
  height?: string | number;
  minHeight?: string | number;
  borderColor?: string;
  primaryColor?: string;
}

export function BorderedGridTable<T>({
  columns,
  data,
  gridTemplateColumns,
  keyExtractor,
  sectionBg,
  textColor,
  subTextColor,
  emptyText = 'No data available',
  title,
  subtitle,
  actionText,
  onActionClick,
  actionInput,
  tabs,
  isLoading = false,
  height = '220px',
  minHeight = '160px',
  borderColor = '#D3D5D9',
  primaryColor = '#7A44E8',
}: BorderedGridTableProps<T>): React.ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [tabs?.active]);

  return (
    <Box
      sx={{
        backgroundColor: sectionBg,
        borderRadius: '12px',
        padding: '16px',
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        height: height || '220px',
        minHeight: minHeight || '160px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Loading Spinner Overlay */}
      {isLoading && (
        <Flex
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            zIndex: 10,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner size={24} sx={{ color: primaryColor }} />
        </Flex>
      )}

      {/* Card Header */}
      {(title || actionText) && (
        <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', mb: '12px' }}>
          <Flex sx={{ alignItems: 'center', gap: '6px' }}>
            {typeof title === 'string' ? (
              <Text sx={{ fontSize: '13px', fontWeight: 600, color: textColor }}>
                {title}{' '}
                {subtitle && (
                  <Text as="span" sx={{ fontSize: '11px', color: subTextColor, fontWeight: 400 }}>
                    {subtitle}
                  </Text>
                )}
              </Text>
            ) : (
              title
            )}
          </Flex>
          {actionText && (
            <Text
              onClick={onActionClick}
              sx={{ fontSize: '12px', color: primaryColor, fontWeight: 600, cursor: 'pointer' }}
            >
              {actionText}
            </Text>
          )}
        </Flex>
      )}

      {/* Action Input Form */}
      {actionInput}

      {/* Dynamic Tabs */}
      {tabs && tabs.list.length > 0 && (
        <Flex
          sx={{
            gap: '16px',
            borderBottom: `1px solid ${borderColor}`,
            mb: '12px',
            pb: '4px',
            overflowX: 'auto',
          }}
        >
          {tabs.list.map((tab) => {
            const isActive = tabs.active === tab;
            return (
              <Text
                key={tab}
                onClick={() => tabs.onChange(tab)}
                sx={{
                  fontSize: '12px',
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? primaryColor : subTextColor,
                  cursor: 'pointer',
                  paddingBottom: '6px',
                  whiteSpace: 'nowrap',
                  borderBottom: isActive ? `2px solid ${primaryColor}` : '2px solid transparent',
                }}
              >
                {tab}
              </Text>
            );
          })}
        </Flex>
      )}

      {/* Inner Table Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          width: '100%',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          borderRadius: '8px',
          border: '1px solid #D3D5D9',
          '&::-webkit-scrollbar': {
            width: '4px',
            height: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(156, 163, 175, 0.4)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(156, 163, 175, 0.7)',
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.4) transparent',
        }}
      >
        {/* Grid Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'stretch',
            borderBottom: '1px solid #D3D5D9',
            color: subTextColor,
            fontSize: '12px',
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            backgroundColor: sectionBg,
            zIndex: 1,
          }}
        >
          {columns.map((col, idx) => {
            const isLast = idx === columns.length - 1;
            const justify =
              col.align === 'left'
                ? 'flex-start'
                : col.align === 'right'
                ? 'flex-end'
                : 'center';

            return (
              <Box
                key={col.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: justify,
                  py: '6px',
                  px: col.px !== undefined ? col.px : '4px',
                  borderRight: isLast ? 'none' : '1px solid #D3D5D9',
                }}
              >
                <Text sx={{ textAlign: col.align || 'center', whiteSpace: 'nowrap' }}>
                  {col.header}
                </Text>
              </Box>
            );
          })}
        </Box>

        {/* Grid Rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {data.map((item, index) => (
            <Box
              key={keyExtractor(item, index)}
              sx={{
                display: 'grid',
                gridTemplateColumns,
                alignItems: 'stretch',
                borderBottom: '1px solid #D3D5D9',
                color: textColor,
                fontSize: '12px',
              }}
            >
              {columns.map((col, idx) => {
                const isLast = idx === columns.length - 1;
                const justify =
                  col.align === 'left'
                    ? 'flex-start'
                    : col.align === 'right'
                    ? 'flex-end'
                    : 'center';

                return (
                  <Box
                    key={col.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: justify,
                      py: '8px',
                      px: col.px !== undefined ? col.px : '4px',
                      borderRight: isLast ? 'none' : '1px solid #D3D5D9',
                      overflow: 'hidden',
                    }}
                  >
                    {col.cell(item, index)}
                  </Box>
                );
              })}
            </Box>
          ))}

          {data.length === 0 && (
            <Flex sx={{ justifyContent: 'center', alignItems: 'center', py: '32px' }}>
              <Text sx={{ color: subTextColor, fontSize: '13px' }}>
                {emptyText}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default BorderedGridTable;
